import React, { useState, useMemo } from 'react';
import { StatusBadge, ActionMenu } from '../../ui/Table';
import { SearchInput } from '../../ui/Input';
import { formatBytes, getSourceName } from './utils';
import { STATUS_VARIANTS } from './constants';
import axiosInstance from '../../api/axiosInstance';

// Compact inline stat card for the dashboard header
function StatItem({ title, value, icon, color = 'gray' }) {
  const colorMap = {
    gray: 'bg-gray-500/10 text-gray-500 dark:bg-gray-400/10 dark:text-gray-400',
    green: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400',
    orange: 'bg-orange-500/10 text-orange-600 dark:bg-orange-400/10 dark:text-orange-400',
    red: 'bg-red-500/10 text-red-600 dark:bg-red-400/10 dark:text-red-400'
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-card border border-border">
      <div className={`flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${colorMap[color]}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground leading-none mb-1.5">{title}</p>
        <p className="text-xl font-bold text-foreground leading-none tabular-nums">{value}</p>
      </div>
    </div>
  );
}

export default function MigrationDashboard({ jobs, loading, error, onRefresh, onViewJob, onRetry, onNewMigration, showNotification }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const stats = useMemo(() => {
    const total = jobs.length;
    const running = jobs.filter(j => j.status === 'running').length;
    const completed = jobs.filter(j => j.status === 'completed').length;
    const failed = jobs.filter(j => j.status === 'failed').length;
    return { total, running, completed, failed };
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    let result = jobs;

    if (filter !== 'all') {
      result = result.filter(j => j.status === filter);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(j =>
        (j.sourceType || '').toLowerCase().includes(q) ||
        (j.jobId || '').toLowerCase().includes(q) ||
        getSourceName(j.sourceType).toLowerCase().includes(q)
      );
    }

    return result;
  }, [jobs, filter, search]);

  const handleDelete = async (jobId) => {
    try {
      await axiosInstance.delete(`/api/business/email-migration/jobs/${jobId}`);
      showNotification?.('Migration job deleted', 'success');
      onRefresh();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to delete job', 'error');
    }
  };

  const handleStop = async (jobId) => {
    try {
      await axiosInstance.post(`/api/business/email-migration/jobs/${jobId}/stop`);
      showNotification?.('Migration stopped', 'success');
      onRefresh();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to stop migration', 'error');
    }
  };

  const handleRetryJob = async (jobId) => {
    try {
      await axiosInstance.post(`/api/business/email-migration/jobs/${jobId}/retry`);
      showNotification?.('Migration restarted', 'success');
      onRetry(jobId);
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to retry migration', 'error');
    }
  };

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'running', label: 'Running' },
    { id: 'completed', label: 'Completed' },
    { id: 'failed', label: 'Failed' },
    { id: 'cancelled', label: 'Cancelled' }
  ];

  const columns = [
    {
      id: 'sourceType',
      header: 'Source',
      render: (value) => (
        <span className="font-medium text-foreground">{getSourceName(value)}</span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      render: (value) => (
        <StatusBadge variant={STATUS_VARIANTS[value] || 'gray'}>{value}</StatusBadge>
      )
    },
    {
      id: 'progress',
      header: 'Progress',
      render: (value, row) => (
        <div className="flex items-center gap-2.5 min-w-[140px]">
          <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                row.status === 'running' ? 'bg-orange-500 animate-pulse' :
                row.status === 'completed' ? 'bg-emerald-500' :
                row.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'
              }`}
              style={{ width: `${value || 0}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">{value || 0}%</span>
        </div>
      )
    },
    {
      id: 'totalUsers',
      header: 'Users',
      render: (value, row) => (
        <span className="text-sm tabular-nums text-muted-foreground">
          {row.completedUsers || 0}/{value || 0}
        </span>
      )
    },
    {
      id: 'transferredData',
      header: 'Data',
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatBytes(value)}</span>
      )
    },
    {
      id: 'startedAt',
      header: 'Started',
      render: (value) => (
        <span className="text-sm text-muted-foreground">
          {value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '--'}
        </span>
      )
    },
    {
      id: 'actions',
      header: '',
      render: (_, row) => (
        <ActionMenu
          items={[
            { label: 'View Details', onClick: () => onViewJob(row.jobId || row._id) },
            ...(row.status === 'running' ? [{ label: 'Stop', variant: 'danger', onClick: () => handleStop(row.jobId || row._id), confirmMessage: 'Stop this migration?' }] : []),
            ...(row.status === 'failed' || row.status === 'cancelled' ? [{ label: 'Retry', onClick: () => handleRetryJob(row.jobId || row._id) }] : []),
            { type: 'divider' },
            ...(row.status !== 'running' ? [{ label: 'Delete', variant: 'danger', onClick: () => handleDelete(row.jobId || row._id), confirmMessage: 'Delete this migration job?' }] : [])
          ]}
          row={row}
        />
      )
    }
  ];

  return (
    <div className="space-y-5">
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatItem
          title="Total Jobs"
          value={stats.total}
          color="gray"
          icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>}
        />
        <StatItem
          title="Completed"
          value={stats.completed}
          color="green"
          icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <StatItem
          title="Running"
          value={stats.running}
          color="orange"
          icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
        />
        <StatItem
          title="Failed"
          value={stats.failed}
          color="red"
          icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
        />
      </div>

      {/* Unified Table Card — filter bar + table in one surface */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Filter Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-3 border-b border-border">
          <div className="flex items-center gap-1">
            {filters.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  filter === f.id
                    ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <SearchInput
              value={search}
              onSearch={setSearch}
              placeholder="Search jobs..."
              className="w-44"
            />
            <button
              onClick={onRefresh}
              className="p-2 text-muted-foreground hover:text-foreground rounded-md hover:bg-accent transition-colors"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className={`px-5 py-3 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider ${
                      col.id === 'actions' ? 'w-12' : ''
                    }`}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i}>
                    {columns.map((col, ci) => (
                      <td key={col.id} className="px-5 py-4">
                        <div
                          className="h-4 bg-muted rounded animate-pulse"
                          style={{ width: col.id === 'actions' ? '32px' : `${55 + ((i * 7 + ci * 13) % 30)}%` }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-5 py-20 text-center">
                    <div className="flex flex-col items-center max-w-xs mx-auto">
                      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
                        <svg className="w-7 h-7 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 19V5a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-foreground mb-1">No migration jobs yet</p>
                      <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
                        Start your first email migration to see jobs listed here.
                      </p>
                      <button
                        onClick={onNewMigration}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Start Migration
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredJobs.map((row, index) => (
                  <tr
                    key={row.jobId || row._id || index}
                    className="cursor-pointer hover:bg-accent/50 transition-colors duration-100"
                    onClick={() => onViewJob(row.jobId || row._id)}
                  >
                    {columns.map((col) => (
                      <td key={col.id} className="px-5 py-3.5 whitespace-nowrap text-sm">
                        {col.render ? col.render(row[col.id], row, index) : row[col.id]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
